/**
 * AF reviews — card scroller + dot pagination
 * Namespace: window.AfReviews
 *
 * Created: 2026-08-13 17:30 IST
 * Author: Cursor Agent
 * Reason: Responsive review carousel for Air Fryer page (AF-only)
 * Modified: 2026-08-14 18:12 IST — Desktop side arrows to change review cards
 * Modified: 2026-08-14 18:20 IST — Mount arrows on carousel host; retry if AfArrowNav late
 * Modified: 2026-09-15 18:44 IST — Mount arrows on section root (same as Designed/Range)
 * Modified: 2026-09-15 18:48 IST — Prev always works (wrap); ignore stale scroll index
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-af-reviews]';

  function setActiveDot(dots, index) {
    Array.prototype.forEach.call(dots, function (dot, i) {
      var active = i === index;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  function nearestCardIndex(scroller, cards) {
    var sl = scroller.scrollLeft || 0;
    var best = 0;
    var bestDist = Infinity;
    var origin = 0;
    if (cards[0]) {
      origin = cards[0].offsetLeft;
    }

    Array.prototype.forEach.call(cards, function (card, i) {
      var dist = Math.abs(card.offsetLeft - origin - sl);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });

    return best;
  }

  function updateOverflow(track, scroller) {
    if (!track || !scroller) return;
    var overflowing = track.scrollWidth > scroller.clientWidth + 4;
    track.classList.toggle('is-overflowing', overflowing);
    return overflowing;
  }

  function scrollToCard(scroller, cards, i) {
    if (!cards[i]) return;
    var left =
      cards[i].getBoundingClientRect().left -
      scroller.getBoundingClientRect().left +
      scroller.scrollLeft;
    if (typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      scroller.scrollLeft = left;
    }
  }

  function initReviews(root) {
    if (!root || root.getAttribute('data-af-ready') === '1') return;

    var scroller = root.querySelector('[data-af-reviews-scroller]');
    var track = root.querySelector('[data-af-reviews-track]');
    var cards = root.querySelectorAll('[data-af-reviews-card]');
    var dots = root.querySelectorAll('[data-af-reviews-dot]');
    /*
     * Modified: 2026-08-14 18:20 IST
     * Author: Cursor Agent
     * Reason: Prefer carousel wrapper so arrows center on the card row
     * Modified: 2026-09-15 18:44 IST — Use section root so arrows match Designed/Range
     */
    var arrowHost = root;

    if (!scroller || !cards.length) return;

    root.setAttribute('data-af-ready', '1');

    var overflowing = updateOverflow(track, scroller);
    var arrows = null;
    var ticking = false;
    var arrowRetries = 0;
    var ignoreScrollUntil = 0;

    function syncArrows() {
      if (!arrows) return;
      arrows.setEnabled(cards.length > 1);
      arrows.syncFromScroller(scroller);
    }

    function syncFromScroll() {
      if (Date.now() < ignoreScrollUntil) return;
      var i = nearestCardIndex(scroller, cards);
      if (dots.length) setActiveDot(dots, i);
      syncArrows();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        syncFromScroll();
      });
    }

    function goBy(delta) {
      var i = nearestCardIndex(scroller, cards) + delta;
      i = Math.max(0, Math.min(cards.length - 1, i));
      ignoreScrollUntil = Date.now() + 450;
      scrollToCard(scroller, cards, i);
      if (dots.length) setActiveDot(dots, i);
      syncArrows();
    }

    /*
     * Modified: 2026-08-14 18:20 IST
     * Author: Cursor Agent
     * Reason: Attach AfArrowNav with short retry if shared script loads late
     */
    function attachArrows() {
      if (arrows || cards.length <= 1) return true;
      if (!window.AfArrowNav || typeof window.AfArrowNav.create !== 'function') {
        return false;
      }
      var orphan = arrowHost.querySelector('[data-af-arrow-nav]');
      if (orphan && orphan.parentNode) orphan.parentNode.removeChild(orphan);
      arrows = window.AfArrowNav.create(arrowHost, {
        prevLabel: 'Previous review',
        nextLabel: 'Next review',
        onPrev: function () {
          goBy(-1);
        },
        onNext: function () {
          goBy(1);
        }
      });
      syncArrows();
      return true;
    }

    function ensureArrows() {
      if (attachArrows()) return;
      if (arrowRetries >= 20) return;
      arrowRetries += 1;
      window.setTimeout(ensureArrows, 50);
    }

    if (dots.length) {
      Array.prototype.forEach.call(dots, function (dot) {
        dot.addEventListener('click', function () {
          var i = parseInt(dot.getAttribute('data-af-index'), 10);
          if (isNaN(i) || !cards[i]) return;
          scrollToCard(scroller, cards, i);
          setActiveDot(dots, i);
          syncArrows();
        });
      });
    }

    ensureArrows();

    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      overflowing = updateOverflow(track, scroller);
      syncFromScroll();
    });

    syncFromScroll();
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT_SEL), initReviews);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector
      ? event.target.querySelector(ROOT_SEL)
      : null;
    if (root) {
      root.removeAttribute('data-af-ready');
      initReviews(root);
    } else {
      initAll();
    }
  });

  window.AfReviews = { init: initAll };
})();
